import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { dashboardApi } from '../../api/dashboard'
import { RefusedError } from '../../api/client'
import { Avatar } from '../../components/Avatar'
import { VoteBadge } from '../../components/VoteButtons'
import { useSession } from '../../hooks/useSession'
import { useSpoke } from '../../hooks/useSpoke'
import { activatedWords, craftLink, daysWords, dayWords, firstName, greeting, pipelineBars, reasonWords, stageRoute, stageWords, strengthClass, weekSentences, widthOf } from '../../lib/dashboard'
import { formatDate, formatMoney } from '../../lib/format'
import { scopeWords } from '../../lib/orgs'
import type { DashboardContact, DashboardMeeting, DashboardOpportunity, DashboardStage, DashboardTies, DashboardView, DashboardWeek } from '../../types/dashboard'
import './dashboard.css'

// The landing dashboard: the cockpit. What the signed-in rep has in flight (the opportunities they activated),
// the ten people to reach next and why, the pipeline by stage, the state of their ties, and the week. Everything
// is the API's reading of their own mail, calendar and pipeline; the browser greets, formats and links.
// Bounded by the org looked at (owner, 2026-10-12: the org is a site-wide filter): under another org the screen shows that
// org's opportunities, pipeline and ties, and the viewer's own next ten and week stay under the viewer's own org.

const DAY = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
const TIME = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })

export function Dashboard() {
  const spoke = useSpoke()
  const { user } = useSession()
  const q = useQuery({ queryKey: ['dashboard', spoke], queryFn: () => dashboardApi.get(spoke), staleTime: 30_000 })

  if (q.isPending) return <p className="muted centered">Loading…</p>
  if (q.isError) {
    return (
      <section className="page dashboard">
        <p className="kicker">Dashboard</p>
        <h1>{greeting(new Date().getHours())}{user ? `, ${firstName(user.displayName)}` : ''}</h1>
        <div className="refusal" role="status">
          <strong>{q.error instanceof RefusedError ? q.error.refusal.reason : 'The dashboard is unavailable'}</strong>
          <p>{q.error instanceof RefusedError ? q.error.refusal.message : q.error.message}</p>
        </div>
      </section>
    )
  }
  return <DashboardScreen view={q.data} name={user?.displayName ?? ''} />
}

/** The page over a view the API returned. Pure, so the render test can hold the clock still. */
export function DashboardScreen({ view, name, now = new Date() }: { view: DashboardView; name: string; now?: Date }) {
  const first = firstName(name)
  // the org looked at is the filter (owner, 2026-10-12); outside your own org the screen shows that org's rows and says so
  const inOrg = view.viewerInOrg !== false
  const whose = scopeWords(view.scope)
  return (
    <section className="page dashboard">
      <header className="deals-head">
        <div>
          <p className="kicker">Dashboard{whose ? ` · ${whose}` : ''}</p>
          <h1>{greeting(now.getHours())}{first ? `, ${first}` : ''}</h1>
          <p className="lede">
            {DAY.format(now)}. {!inOrg
              ? `You are looking at ${whose ?? 'another org'}: their opportunities in flight, their pipeline and their ties. Your own next ten and your week are shown under your own org.`
              : view.connected
                ? 'What you have in flight, who to reach next and why, and the shape of your week — all read from your own mail, calendar and pipeline.'
                : 'Scout reads your own mail and calendar to say what is in flight and who to reach next; it needs a mailbox first.'}
          </p>
        </div>
      </header>

      {!inOrg ? (
        <>
          <InFlight items={view.inFlight} />
          <div className="dash-row">
            <Pipeline stages={view.pipeline} />
            <Relationships ties={view.ties} />
          </div>
        </>
      ) : !view.connected ? <ConnectCard /> : (
        <>
          <StatStrip view={view} />
          <InFlight items={view.inFlight} />
          <NextTen items={view.next} prepared={view.prepared} />
          <div className="dash-row">
            <Pipeline stages={view.pipeline} />
            <Relationships ties={view.ties} />
            <ThisWeek week={view.week} meetings={view.meetings} />
          </div>
        </>
      )}
    </section>
  )
}

/* ── no mailbox yet: the one card, and nothing else ── */

function ConnectCard() {
  return (
    <div className="card dash-connect">
      <h2>Connect your mailbox</h2>
      <p className="muted">
        Scout reads the mail you send and receive to find what is in flight, who is waiting on you, and who has gone quiet.
        Until a mailbox is connected there is nothing here to show.
      </p>
      <p className="actions"><Link className="button primary" to="/mailbox">Connect a mailbox</Link></p>
    </div>
  )
}

/* ── the stat strip: five numbers, each a door ── */

function StatStrip({ view }: { view: DashboardView }) {
  return (
    <nav className="glance dash-glance" aria-label="At a glance">
      <Tile n={view.inFlight.length} label="in flight" to="/opportunities" title="Opportunities you have activated" />
      <Tile n={view.next.length} label="to reach next" to="/contacts" title="The people ranked for you to contact next" />
      <Tile n={view.week.sent} label="sent this week" to="/outbox" title="Messages that left your outbox in the last seven days" />
      <Tile n={view.week.replied} label="replies this week" to="/outbox" title="Messages of yours that drew a reply in the last seven days" />
      <Tile n={view.week.meetings} label="meetings this week" to="/meetings" title="Rooms you were in over the last seven days" />
    </nav>
  )
}

function Tile({ n, label, to, title }: { n: number; label: string; to: string; title: string }) {
  return (
    <Link className="glance-stat dash-tile" to={to} title={title}>
      <strong>{n.toLocaleString()}</strong>
      <span>{label}</span>
    </Link>
  )
}

/* ── opportunities in flight ── */

function InFlight({ items }: { items: DashboardOpportunity[] }) {
  return (
    <section className="card dash-section" aria-labelledby="dash-flight-h">
      <div className="card-head">
        <div>
          <h2 id="dash-flight-h">Opportunities in flight</h2>
          <p className="muted">The ones you have picked up: set up by hand, drafted on, or carrying a play. Newest activity first.</p>
        </div>
        <Link className="button small" to="/opportunities">All opportunities</Link>
      </div>
      {items.length === 0 ? (
        <p className="muted dash-empty">Nothing is in flight yet. <Link to="/opportunities">Pick an opportunity to work</Link> and it appears here.</p>
      ) : (
        <div className="dash-flight">
          {items.map((o) => <OpportunityCard key={o.engagementId} o={o} />)}
        </div>
      )}
    </section>
  )
}

function OpportunityCard({ o }: { o: DashboardOpportunity }) {
  const lead = o.people[0]
  return (
    <article className={`dash-opp stage-${o.stage}${o.nextStepOverdue ? ' overdue' : ''}`}>
      <header className="dash-opp-head">
        <p className="dash-opp-company">
          <strong>{o.company}</strong>
          <span className={`tag stage ${o.stage}`}>{stageWords(o.stage)}</span>
        </p>
        <p className="dash-opp-subject">{o.subject}</p>
      </header>
      <p className="dash-opp-facts">
        {o.amount != null && <span className="dash-opp-amount">{formatMoney(o.amount)}</span>}
        <span className="muted">{o.lastActivityOn ? `last activity ${dayWords(o.lastActivityOn)}` : 'no dated activity'}</span>
      </p>
      {o.nextStep && (
        <p className={`dash-next-step${o.nextStepOverdue ? ' overdue' : ''}`}>
          <span className="dash-next-label">Next</span>
          {o.nextStep}
          {o.nextStepBy && <span className="dash-next-by"> by {dayWords(o.nextStepBy)}</span>}
          {o.nextStepOverdue && <span className="dash-next-flag"> · overdue</span>}
        </p>
      )}
      {o.people.length > 0 && (
        <p className="dash-opp-people">
          {o.people.slice(0, 3).map((p) => <Link key={p.personId} className="chip person" to={`/contacts/${encodeURIComponent(p.personId)}`}>{p.name}</Link>)}
          {o.people.length > 3 && <span className="chip">+{o.people.length - 3}</span>}
        </p>
      )}
      <p className="muted small dash-opp-activated">
        {activatedWords(o)}
        {o.evidence && <> · <span className={`tag evidence-tag ${o.evidence.score >= 0.7 ? 'strong' : o.evidence.score >= 0.4 ? 'fair' : 'weak'}`} title={`Confidence from the facts on the row: ${o.evidence.basis.join('; ')}.`}>{Math.round(o.evidence.score * 100)}%</span></>}
      </p>
      <p className="actions dash-opp-actions">
        <Link className="button small primary" to={craftLink(o.companyId, lead?.personId, o.engagementId)}>Craft</Link>
        <Link className="button small" to={`/opportunities/${encodeURIComponent(o.engagementId)}`}>Open</Link>
      </p>
    </article>
  )
}

/* ── the next ten, and why ── */

function NextTen({ items, prepared }: { items: DashboardContact[]; prepared: boolean }) {
  return (
    <section className="card dash-section" aria-labelledby="dash-next-h">
      <div className="card-head">
        <div>
          <h2 id="dash-next-h">{items.length === 10 ? 'Next ten to reach out to, and why' : items.length > 0 ? `Next ${items.length} to reach out to, and why` : 'Who to reach out to next, and why'}</h2>
          <p className="muted">Strongest reason first. Each row says what it is standing on; Craft opens the workbench on that person.</p>
        </div>
        <Link className="button small" to="/outreach">Outreach</Link>
      </div>
      {!prepared && (
        <p className="muted dash-empty dash-not-ranked">
          Scout has not ranked anyone yet. <Link to="/outreach">Open Outreach</Link> to prepare the list; it ranks everyone you correspond with by what is owed and what has gone quiet.
        </p>
      )}
      {prepared && items.length === 0 && (
        <p className="muted dash-empty">Nothing owed today: nobody is waiting on you and nothing has gone quiet. <Link to="/outreach">Outreach</Link> has who that covers.</p>
      )}
      {items.length > 0 && (
        <ol className="dash-next">
          {items.map((c, i) => <ContactRow key={c.personId} c={c} rank={i + 1} />)}
        </ol>
      )}
      {items.length > 0 && (
        <p className="dash-foot"><Link to="/outreach">See everyone</Link></p>
      )}
    </section>
  )
}

function ContactRow({ c, rank }: { c: DashboardContact; rank: number }) {
  const where = [c.title, c.company, c.location].filter((x): x is string => !!x)
  // a name made from the address or half a name is said with the address beside it, never passed off as a name
  // (owner, 2026-10-12: "the names are not easily visible, such as cmercer, charlie")
  const madeUp = !!c.nameBasis && c.nameBasis !== 'file' && c.nameBasis !== 'file-inferred'
  return (
    <li className={`dash-person ${c.reasonKind}`}>
      <span className="dash-rank" aria-hidden="true">{rank}</span>
      <Avatar name={c.name} photoUrl={c.photoUrl} size={40} className="flat" />
      <div className="dash-person-body">
        <p className="dash-person-name">
          <Link to={`/contacts/${encodeURIComponent(c.personId)}`}>{c.name}</Link>
          {madeUp && c.email && <span className="muted small dash-person-address" title="No full name on file yet: this is their address">{c.email}</span>}
          <VoteBadge vote={c.vote} />
        </p>
        {where.length > 0 && <p className="muted small dash-person-where">{where.join(' · ')}</p>}
        <p className="dash-reason">
          <span className="dash-reason-text">{c.reason}</span>
          <span className={`tag reason ${c.reasonKind}`}>{reasonWords(c.reasonKind)}</span>
        </p>
        <p className="dash-person-meta">
          <span className="muted small">{daysWords(c.daysSinceContact)}</span>
          {c.dueOn && <span className="muted small">due {dayWords(c.dueOn)}</span>}
          {c.openPursuit && (
            <span className="chip pursuit">
              {c.openPursuit.subject}
              <span className={`tag stage ${c.openPursuit.stage}`}>{stageWords(c.openPursuit.stage)}</span>
            </span>
          )}
          {c.nextStep && (
            <span className={`dash-next-step small${c.nextStep.overdue ? ' overdue' : ''}`}>
              <span className="dash-next-label">Next</span>
              {c.nextStep.text}
              {c.nextStep.by && <span className="dash-next-by"> by {dayWords(c.nextStep.by)}</span>}
              {c.nextStep.overdue && <span className="dash-next-flag"> · overdue</span>}
            </span>
          )}
          {c.strength != null && (
            <span className="strength-bar dash-strength" title={`Tie strength ${Math.round(c.strength * 100)} of 100`}>
              <span className={`strength-fill ${strengthClass(c.strength)}`} style={{ width: widthOf(c.strength) }} />
            </span>
          )}
        </p>
      </div>
      <div className="dash-person-actions">
        {c.companyId
          ? <Link className="button small primary" to={craftLink(c.companyId, c.personId, c.openPursuit?.engagementId)}>Craft</Link>
          : <Link className="button small" to={`/contacts/${encodeURIComponent(c.personId)}`}>Open card</Link>}
      </div>
    </li>
  )
}

/* ── the pipeline by stage ── */

function Pipeline({ stages }: { stages: DashboardStage[] }) {
  const bars = pipelineBars(stages)
  return (
    <section className="card dash-panel" aria-labelledby="dash-pipe-h">
      <h2 id="dash-pipe-h">Pipeline</h2>
      {bars.length === 0 ? <p className="muted">No pursuit on file yet.</p> : (
        <ol className="dash-bars">
          {bars.map((b) => (
            <li key={b.stage} className={`dash-bar-row stage-${b.stage}`}>
              <Link className="dash-bar-label" to={stageRoute(b.stage)}>{stageWords(b.stage)}</Link>
              <span className="dash-bar" aria-hidden="true"><span className="dash-bar-fill" style={{ width: widthOf(b.countFraction) }} /></span>
              <span className="dash-bar-n">{b.count}</span>
              <span className="dash-bar-amount muted small">{b.amount > 0 ? formatMoney(b.amount) : ''}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

/* ── relationships ── */

function Relationships({ ties }: { ties: DashboardTies }) {
  const rows: { n: number; label: string; title: string }[] = [
    { n: ties.total, label: 'people you know', title: 'Everyone on file you have corresponded with' },
    { n: ties.strong, label: 'strong ties', title: 'Ties with mail both ways and recent contact' },
    { n: ties.quiet, label: 'gone quiet', title: 'Warm ties with nothing either way for a while' },
    { n: ties.moved, label: 'may have moved', title: 'People who now write from somewhere new' },
    { n: ties.placed, label: 'placed', title: 'People who write from a known company' },
  ]
  return (
    <section className="card dash-panel" aria-labelledby="dash-ties-h">
      <h2 id="dash-ties-h">Relationships</h2>
      <ul className="dash-ties">
        {rows.map((r) => (
          <li key={r.label}>
            <Link to="/contacts/list" title={r.title}>
              <strong>{r.n.toLocaleString()}</strong>
              <span>{r.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

/* ── this week ── */

function ThisWeek({ week, meetings }: { week: DashboardWeek; meetings: DashboardMeeting[] }) {
  return (
    <section className="card dash-panel" aria-labelledby="dash-week-h">
      <h2 id="dash-week-h">This week</h2>
      {weekSentences(week).map((s) => <p key={s} className="dash-week-line">{s}</p>)}
      {meetings.length > 0 && (
        <>
          <p className="kicker dash-meetings-kicker">Coming up</p>
          <ul className="dash-meetings">
            {meetings.map((m) => <MeetingRow key={m.meetingId} m={m} />)}
          </ul>
        </>
      )}
    </section>
  )
}

function MeetingRow({ m }: { m: DashboardMeeting }) {
  const at = new Date(m.startsAt)
  const when = Number.isNaN(at.getTime()) ? m.startsAt : `${formatDate(m.startsAt)} · ${TIME.format(at)}`
  return (
    <li className="dash-meeting">
      <span className="dash-meeting-when">{when}</span>
      <span className="dash-meeting-body">
        <Link to="/meetings" className="dash-meeting-subject">{m.subject}</Link>
        {(m.company || m.people.length > 0) && (
          <span className="muted small">{[m.company, m.people.join(', ')].filter(Boolean).join(' · ')}</span>
        )}
      </span>
    </li>
  )
}
